/**
 * vorgangeListService: provides activitylist & other functionality related to vorgange services
 * @ngdoc service
 * @name vorgangeListService
 * @memberof src/store/services
 */
import { config } from "../config";
import axios from "axios";
import moment from "moment";

// utils
import { convertStringToDate, encodeSearchValue } from "../../../helpers/utils";

const dateColumns = ["gewünschtesUmsetzungsdatum", "letzteAnderung"];

const FIELD_COLUMNS = ["vorgangsart", "status"];

/**
 * get vorgange list based on query parameter
 * @memberof vorgangeListService
 * @param {object} query
 */
function getVorgangeList(query, includeHiddenFilters) {
  let errorFilter = "";

  if (query?.error) {
    errorFilter = `&error=${query?.error}`;
  } else if (query?.archived) {
    errorFilter = `&archived=${query?.archived}`;
  } else if (query?.duplicate) {
    errorFilter = `&duplicate=${query?.duplicate}`;
  } else if (query?.unread) {
    errorFilter = `&unread=${query?.unread}`;
  } else if (query?.qesCompleted) {
    errorFilter = `&qesCompleted=${query?.qesCompleted}`;
  } else if (query?.nichtMarkiert) {
    errorFilter = `&flag=nichtMarkiert`;
  } else if (query?.wichtig) {
    errorFilter = `&flag=wichtig`;
  } else if (query?.nichtDurchfuhrbar) {
    errorFilter = `&flag=nichtDurchfuhrbar`;
  } else if (query?.erledigt) {
    errorFilter = `&flag=erledigt`;
  }

  let url = `${config.apiUrl}/vorgangs?size=${query?.pageSize || 50}&page=${
    query?.page || 0
  }`;

  if (query?.orderBy && query?.orderDirection !== "") {
    url += `&sort=${query?.orderBy?.field}&order=${query?.orderDirection}`;
  }

  let filters = "";
  //handle filter
  if (query?.filters && query?.filters?.length > 0) {
    query?.filters?.map((filter) => {
      if (filters !== "") {
        filters += "&";
      }
      if (dateColumns?.includes(filter.column.field)) {
        filter.value = moment(filter.value).format("YYYY-MM-DD");
      }

      let filterValue = filter.value ? encodeSearchValue(filter.value) : "";

      if (FIELD_COLUMNS?.includes(filter.column.field)) {
        if (filter.value?.length > 0) {
          filterValue = filter.value.join(",");
        } else {
          filterValue = "";
        }
      }

      return (filters +=
        filterValue &&
        `${filter.column.field}${filter.operator}${filterValue}`);
    });
    filters = `${filters}`;
  }

  if (errorFilter) {
    filters += errorFilter;
  }

  if (includeHiddenFilters) {
    filters += `&includeHiddenFilters=${includeHiddenFilters}`;
  }

  // Set default headers for X-Filter query parameter
  axios.defaults.headers.common["X-Filter"] = filters || "";

  return axios
    .get(url, {
      timeout: 120000,
    }) //Giving it 120 secs Timeout,because sometimes filtering in BE takes time because of Database Computations
    .then((resp) => {
      if (resp.data) {
        resp.data.content.map((row) => {
          row["letzteAnderungString"] = row.letzteAnderung;
          row.letzteAnderung = row.letzteAnderung
            ? convertStringToDate(row.letzteAnderung)
            : "";
          return row;
        });
        return resp.data;
      }
    })
    .catch((error) => {
      return error;
    });
}

/**
 * get featch details when click on vogangelist's row
 * @memberof vorgangeListService
 * @param {string} id
 */
function getPullRequestDetails(id) {
  const url = `${config.apiUrl}/iteos/${id}/pull-request-response`;

  return axios
    .get(url)
    .then((resp) => {
      if (resp.data && resp.data.dateTime) {
        resp.data.dateTime = moment(resp.data.dateTime).format(
          "DD.MM.YYYY HH:MM:SS"
        );
      }
      return resp;
    })
    .catch((error) => {
      return error;
    });
}
/**
 * export row details in xml
 * @memberof vorgangeListService
 * @param {string} id
 */
function exportDetailsInXml(id) {
  const url = `${config.apiUrl}/vorgangs/${id}/xml`;
  return axios
    .get(url, { responseType: "blob" })
    .then((resp) => {
      return resp.data;
    })
    .catch((error) => {
      return error;
    });
}

function readRecord(ids) {
  const url = `${config.apiUrl}/vorgangs/read`;
  return axios
    .put(url, ids)
    .then((resp) => {
      return resp;
    })
    .catch((error) => {
      return error;
    });
}

async function requestXmlLog(requestId) {
  const url = `${config.apiUrl}/vorgangs/${requestId}/access-artifacts`;

  try {
    const resp = await axios.put(url);
    return resp;
  } catch (error) {
    return error;
  }
}

async function getSupportKBA(kbaSupportId) {
  const url = `${config.apiUrl}/kba-support/${kbaSupportId}`;

  try {
    const resp = await axios.get(url);
    return resp.data;
  } catch (error) {
    return error;
  }
}
async function checkRegistrationStatus(requestId) {
  const url = `${config.apiUrl}/vorgangs/${requestId}/registration/status`;

  try {
    const resp = await axios.post(url);
    return resp;
  } catch (error) {
    return error;
  }
}
async function updateFlag(requestId, flag) {
  const url = `${config.apiUrl}/vorgangs/${requestId}/flag`;
  try {
    const resp = await axios.patch(url, { flag: flag });
    return resp;
  } catch (error) {
    return error;
  }
}

// exports the object of methods
export const vorgangeListService = {
  getVorgangeList,
  getPullRequestDetails,
  exportDetailsInXml,
  readRecord,
  requestXmlLog,
  getSupportKBA,
  checkRegistrationStatus,
  updateFlag,
};
